'use client';

/**
 * The palette: pick a shape or a step type, then click the canvas to place it.
 *
 * WHY ARM-THEN-CLICK RATHER THAN DRAG-AND-DROP
 *
 * The canvas already owns pointer events for moving boxes and panning. A third
 * drag source competing for them is where a canvas starts dropping gestures —
 * and a shape that sometimes lands and sometimes does not is worse than one
 * that always takes two deliberate clicks. Arming is also reversible: Escape,
 * or clicking the armed tile again.
 *
 * SHAPES AND TYPES ARE THE SAME LIST, DELIBERATELY SEPARATED
 *
 * A step type carries meaning — a decision branches, a form collects something.
 * A shape is only how it is drawn. They are offered apart because somebody
 * sketching a process reaches for "a diamond here" before they know which of
 * the eight branching step types they want, and forcing that choice first is
 * what makes people give up and draw it somewhere else.
 *
 * Choosing a shape alone creates a task step drawn in that shape. Choosing a
 * type gets the shape its type implies, and no override is stored — so the
 * default stays in one place and a step drawn as a diamond because it IS a
 * decision keeps following the type if the default ever changes.
 */

import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Shapes } from 'lucide-react';

import { STEP_TYPE_CATEGORIES, stepCategoryLabel, stepTypeDescription, stepTypeLabel } from './StepTypePicker';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { CanvasShape } from './canvasGeometry';

export interface PaletteSelection {
  label: string;
  /** Set when a step TYPE was chosen. The canvas seeds the new step with it. */
  stepTypeValue?: string;
  /** Set only when a SHAPE was chosen on its own, so it is stored as an
   *  override. A type's own shape is derived, never written. */
  shape?: CanvasShape;
}

const SHAPES: Array<{ shape: CanvasShape; label: string; hint: string }> = [
  { shape: 'rectangle', label: 'Step', hint: 'Something somebody does' },
  { shape: 'diamond', label: 'Decision', hint: 'The flow goes two ways' },
  { shape: 'rounded', label: 'Wait', hint: 'Time passes, or a reply is awaited' },
  { shape: 'terminator', label: 'Start / End', hint: 'Where the process begins or stops' },
  { shape: 'parallelogram', label: 'Data', hint: 'Something is read or written' },
  { shape: 'note', label: 'Note', hint: 'A remark beside the flow' },
];

/** A small drawing of the shape, so the palette shows what you get rather than
 *  naming it. The canvas uses the same clip-paths. */
const ShapeGlyph: React.FC<{ shape: CanvasShape; active: boolean }> = ({ shape, active }) => {
  const base: React.CSSProperties = {
    width: 34, height: 22, background: active ? '#111827' : '#e5e7eb',
    border: `1px solid ${active ? '#111827' : '#cbd5e1'}`, flexShrink: 0,
  };
  const byShape: Record<CanvasShape, React.CSSProperties> = {
    rectangle: { borderRadius: 3 },
    rounded: { borderRadius: 11 },
    terminator: { borderRadius: 11 },
    diamond: { clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' },
    parallelogram: { clipPath: 'polygon(14% 0%, 100% 0%, 86% 100%, 0% 100%)' },
    note: { clipPath: 'polygon(0 0, 100% 0, 100% 70%, 78% 100%, 0 100%)' },
  };
  return <div style={{ ...base, ...byShape[shape] }} />;
};

interface StepShapePaletteProps {
  armed: PaletteSelection | null;
  onArm: (selection: PaletteSelection | null) => void;
}

/** What a tile puts on the drag, and what the canvas reads off the drop. One
 *  constant so a rename cannot leave the two halves disagreeing silently — the
 *  drop would simply never fire and the shape would spring back. */
export const SHAPE_DRAG_TYPE = 'application/x-weavein-shape';

export const StepShapePalette: React.FC<StepShapePaletteProps> = ({ armed, onArm }) => {
  const { t: tr } = useAppTranslation();
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  /** Collapsed state is remembered: on a laptop the palette costs a fifth of
   *  the canvas, and somebody who has finished placing shapes wants that back
   *  and wants it to stay back. */
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('wf-palette-collapsed') === 'yes';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem('wf-palette-collapsed', collapsed ? 'yes' : 'no');
    } catch {
      /* a remembered preference is not worth failing a render over */
    }
  }, [collapsed]);

  /** Drag a tile onto the canvas. The canvas reads this on drop, places the
   *  shape there and opens its editor — so one gesture both puts the step
   *  somewhere and starts configuring it. Clicking without dragging still
   *  arms, because a click is a shorter gesture and some people prefer it. */
  const startDrag = (e: React.DragEvent, selection: PaletteSelection) => {
    e.dataTransfer.setData(SHAPE_DRAG_TYPE, JSON.stringify(selection));
    e.dataTransfer.effectAllowed = 'copy';
  };

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        title="Show the shapes palette"
        style={{
          width: 40, flexShrink: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 8, padding: '10px 0', cursor: 'pointer',
          background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, color: '#6b7280',
        }}
      >
        <ChevronRight className="w-4 h-4" />
        <Shapes className="w-4 h-4" />
        <span style={{ writingMode: 'vertical-rl', fontSize: 11, fontWeight: 600, letterSpacing: .5 }}>
          Shapes
        </span>
      </button>
    );
  }

  const tile = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
    padding: '7px 8px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
    border: `1px solid ${active ? '#111827' : 'transparent'}`,
    background: active ? '#f3f4f6' : 'transparent', color: '#374151',
  });

  const toggle = (next: PaletteSelection) => {
    const same =
      armed &&
      armed.shape === next.shape &&
      armed.stepTypeValue === next.stepTypeValue;
    onArm(same ? null : next);
  };

  return (
    <div style={{
      width: 208, flexShrink: 0, overflowY: 'auto', padding: 10,
      background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: .5 }}>
          Shapes
        </span>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          title="Hide the shapes palette"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af', padding: 2 }}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>
      <p style={{ fontSize: 10, color: '#9ca3af', margin: '0 0 8px', lineHeight: 1.3 }}>
        Drag one onto the canvas, or click it and then click where it goes.
      </p>
      {SHAPES.map((s) => {
        const active = armed?.shape === s.shape && !armed?.stepTypeValue;
        return (
          <button
            key={s.shape}
            type="button"
            title={s.hint}
            style={tile(active)}
            draggable
            onDragStart={(e) => startDrag(e, { label: s.label, shape: s.shape })}
            onClick={() => toggle({ label: s.label, shape: s.shape })}
          >
            <ShapeGlyph shape={s.shape} active={active} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 600 }}>{s.label}</span>
              <span style={{ display: 'block', fontSize: 10, color: '#9ca3af', lineHeight: 1.2 }}>{s.hint}</span>
            </span>
          </button>
        );
      })}

      <div style={{
        fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase',
        letterSpacing: .5, margin: '14px 0 6px',
      }}>
        Step types
      </div>
      {STEP_TYPE_CATEGORIES.map((cat) => {
        const open = openCategory === cat.id;
        return (
          <div key={cat.id}>
            <button
              type="button"
              style={{ ...tile(false), fontWeight: 600 }}
              onClick={() => setOpenCategory(open ? null : cat.id)}
            >
              {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {cat.icon}
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{stepCategoryLabel(tr, cat)}</span>
            </button>
            {open && (
              <div style={{ paddingLeft: 10 }}>
                {cat.types.map((t) => {
                  const active = armed?.stepTypeValue === t.value;
                  const label = stepTypeLabel(tr, t);
                  return (
                    <button
                      key={t.value}
                      type="button"
                      title={stepTypeDescription(tr, t)}
                      style={tile(active)}
                      draggable
                      onDragStart={(e) => startDrag(e, { label, stepTypeValue: t.value })}
                      onClick={() => toggle({ label, stepTypeValue: t.value })}
                      data-testid={`palette-step-${t.value}`}
                    >
                      {t.icon}
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StepShapePalette;
